import React from "react";
import { useTranslations } from "../../../hooks/use-translations";
import _ from "lodash";
import { useLanguage } from "../../../hooks/use-language";

/**
 * Componente contenedor de la tarjeta del plan. 
 * Solo puede tener 2 tipos de hijos directos, box.columnLeft y box.columnRight
 * @param {JSX.Element} children -hijos directos (box.columnLeft y box.columnRight)
 * @param {string} label - indicador de accesibilidad, indica el tipo de plan
 * @param {string} className - cadena de clases css que se pueden aplicar al componente
 * @returns {JSX.Element} box
 */
const box = ({ children, label, className, ...props }) => {
    return (
        <article
            {...props}
            aria-label={label ? label : 'plan casa'}
            className={`PlanCard-box d-md-flex ${className && className}`}
        >
            {
                React.Children.toArray(children).filter(element => element?.type === columnLeft)
            }
            {
                React.Children.toArray(children).filter(element => element?.type === columnRight)
            }
        </article>
    )
}


/**
 * Columna izquierda contiene la imagen y el nombre del plan
 * @param {Element} children - Elemento con el nombre e información extra del plan
 * @param {string} className - cadena de clases css que se pueden aplicar al componente. Color de fondo
 * @param {string} image - ruta de la imagen
 * @returns {JSX.Element} columnLeft
 */
const columnLeft = ({ children, className, image }) => {
    return (
        <div className={`col-md-5 px-0 column-left ${className && className}`}>
            <div
                className="img-card order-md-2"
                style={{ backgroundImage: `url(${image ? image : '/img/no-imagen3.png'})` }}
            ></div>
            <div className="order-md-1 p-4 h-50 d-flex align-items-center">
                {children}
            </div>

        </div>
    )
}


/**
 * Columna derecha contiene los detalles, el precio y el botón de compra
 * @param {JSX.Element} children -hijos directos (columnRight.details, columnRight.PriceDescription y columnRight.getButtons)
 * @param {string} className - cadena de clases css que se pueden aplicar al componente. Color de fondo
 * @returns {JSX.element} columnRight
 */
const columnRight = ({ children, className }) => {
    return (
        <section className={`col-md-7 p-3 d-flex flex-column  ${className && className}`}>
            {
                React.Children.toArray(children).filter(element => element?.type === details)
            }

            <div className="d-lg-flex align-items-center justify-content-between mt-auto px-md-3">
                {
                    React.Children.toArray(children).filter(element => element?.type === PriceDescription)
                }

                <div className="">
                    {
                        React.Children.toArray(children).filter(element => element?.type === getButtons)
                    }
                </div>
            </div>
            <div className="mt-3 px-4">
                <PricementDisclaimer />
            </div>
        </section>
    )
}

/**
 * Descripción del plan 
 * @param {element} children - contenido descripción
 * @param {string} className - cadena de clases css que se pueden aplicar al div
 * @returns {JSX.Element} details
 */
const details = ({ children, className }) => {
    return <div className={className && className}>{children}</div>
}

/**
 * Precio del plan
 * @param {{
  price: {usd: string, cop: string}, 
  discount: {activado: boolean, usd: {cantidad: number, precio: number}, cop: {cantidad: number, precio: number}}, 
  academySlot: React.ReactNode
   }} params
 * @param price.usd - precio en dolares
 * @param price.cop - precio en pesos colombianos
 * @param discount.activado - indica si hay descuento
 * @param discount.usd.cantidad -  descuento en porcentaje
 * @param discount.usd.precio - precio con descuento en dolares
 * @param discount.cop.cantidad - descuento en porcentaje
 * @param discount.cop.precio - precio con descuento en pesos colombianos
 * @param academySlot - slot para elementos de academia
 * @returns {JSX.Element} PriceDescription
 */
const PriceDescription = ({ price, discount, academySlot }) => {
	const usdPrice = price?.usd;
	const copPrice = price?.cop;
	const isDiscount = discount?.activado;

    const lang = useLanguage();
    const texts = useTranslations({ section: "tienda_makemake", lang: lang });

	return (
		<div className={`row p-0 m-0 mb-3 mb-lg-0`}>
			<div className="mx-2">
				{isDiscount && (
					<p className="font-weight-normal">
						<span style={{ fontSize: ".7em", textDecorationLine: "line-through" }}>
							${usdPrice} USD
						</span>
						<span className="font-weight-bold" style={{ fontSize: ".7em" }}>
							{" "}
							{discount?.usd?.cantidad} OFF
						</span>
					</p>
				)}
				<p className="font-weight-bold onprimary--color secondary-font">
                    <span className="sr-only">
                        {_.get(texts, 'priceLabel.dollars', 'Precio en dólares:')}
                    </span>
					<span className="currency">USD</span>
					<br />
					<span className="text-20">
						${isDiscount ? discount?.usd?.precio : usdPrice}
					</span>
				</p>
			</div>
			<div className="mx-2">
				{isDiscount && (
					<p className="font-weight-normal">
						<span style={{ fontSize: ".7em", textDecorationLine: "line-through" }}>
							${copPrice} COP
						</span>
						<span className="font-weight-bold" style={{ fontSize: ".7em" }}>
							{" "}
							{discount?.cop?.cantidad} OFF
						</span>
					</p>
				)}
				<p className="font-weight-bold onprimary--color secondary-font">
                    <span className="sr-only">
                        {_.get(texts, 'priceLabel.dollars', 'Precio en pesos colombianos:')}
                    </span>
					<span className="currency">COP</span>
					<br />
					<span className="text-20">
						${isDiscount ? discount?.cop?.precio : copPrice}
					</span>
				</p>
			</div>
            {academySlot}
		</div>
	);
};

/**
 * botones del plan (trailer y comprar o adquirir)
 * @param {element} children - contenido del botón 
 * @param {string} className - cadena de clases css que se pueden aplicar al div
 * @returns {JSX.Element} details
 */
const getButtons = ({ children, className }) => {
    return (
        <footer className={`${className && className} d-flex align-items-center justify-content-lg-end`}>{children}</footer>
    )
}

const PricementDisclaimer = ({className}) => {
        const lang = useLanguage();
        const texts = useTranslations({ section: "tienda_makemake", lang: lang });
    
	return (
		<p
			style={{ fontSize: "12px" }}
			className="w-100 onbackground--color"
		>
            {
                _.get(texts, 'tienda.price_disclaimer', 'Precio cobrado en USD. Valor aproximado en COP sujeto a tasa de cambio.')
            }
			
		</p>
	);
};
/**
 * Objeto que retorna los componentes que conforman las tarjetas en PLAN CASA
 */
export const PlanCard = {
    box,
    columnLeft,
    columnRight,
    details,
    PriceDescription,
    getButtons,
    PricementDisclaimer
}